import { FLAP_DETENTS } from '../sim/constants';

/**
 * Holds the current pilot control commands, all normalised.
 *
 * The primary flight controls (aileron/elevator/rudder) are driven from a
 * *target* value (written every frame by the joystick, or nudged toward a
 * held key state by the keyboard) and smoothed toward it, so keyboard and
 * joystick feel consistent. Throttle/mixture/trim/flaps are persistent
 * settings changed incrementally.
 */
export interface ControlSurfaces {
  aileron: number; // -1 (left) .. +1 (right)
  elevator: number; // -1 (nose down) .. +1 (nose up)
  rudder: number; // -1 (left) .. +1 (right)
  throttle: number; // 0..1
  mixture: number; // 0..1
  trim: number; // -1..1 (nose up positive)
  flapDetent: number; // index into FLAP_DETENTS
  brakes: number; // 0..1 — both main wheels together (keyboard toggle / digital pad button)
  // Independent per-wheel analog brake, e.g. from HID toe-brake axes — combined
  // with `brakes` (whichever is stronger wins) rather than replacing it, so the
  // digital "both wheels" brake keeps working even with pedals bound.
  brakeLeft: number; // 0..1
  brakeRight: number; // 0..1
  gearDown: boolean; // no effect on fixed-gear aircraft (e.g. the C172S)
}

export class Controls {
  aileron = 0;
  elevator = 0;
  rudder = 0;
  throttle = 0;
  mixture = 0.9;
  trim = 0;
  flapDetent = 0;
  brakes = 0;
  brakeLeft = 0;
  brakeRight = 0;
  parkingBrake = false;
  gearDown = true; // fixed on aircraft without HAS_RETRACTABLE_GEAR

  // Targets set by the input layer each frame (default self-centering to 0).
  aileronTarget = 0;
  elevatorTarget = 0;
  rudderTarget = 0;

  /** Smooth the primary controls toward their targets (called each sim step). */
  update(dt: number): void {
    const rate = 6; // per second — brisk but not instantaneous
    const k = Math.min(1, dt * rate);
    this.aileron += (this.aileronTarget - this.aileron) * k;
    this.elevator += (this.elevatorTarget - this.elevator) * k;
    this.rudder += (this.rudderTarget - this.rudder) * k;
  }

  adjustThrottle(delta: number): void {
    this.throttle = clamp01(this.throttle + delta);
  }
  adjustMixture(delta: number): void {
    this.mixture = clamp01(this.mixture + delta);
  }
  adjustTrim(delta: number): void {
    this.trim = clamp(this.trim + delta, -1, 1);
  }
  flapsUp(): void {
    this.flapDetent = Math.max(0, this.flapDetent - 1);
  }
  flapsDown(): void {
    this.flapDetent = Math.min(FLAP_DETENTS.length - 1, this.flapDetent + 1);
  }
  toggleGear(): void {
    this.gearDown = !this.gearDown;
  }

  snapshot(): ControlSurfaces {
    return {
      aileron: this.aileron,
      elevator: this.elevator,
      rudder: this.rudder,
      throttle: this.throttle,
      mixture: this.mixture,
      trim: this.trim,
      flapDetent: this.flapDetent,
      brakes: this.parkingBrake ? 1 : this.brakes,
      brakeLeft: this.brakeLeft,
      brakeRight: this.brakeRight,
      gearDown: this.gearDown,
    };
  }
}

function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}
function clamp01(v: number): number {
  return clamp(v, 0, 1);
}
